import type { ColorScheme } from '../enums/color-scheme.enum.js'
import { accentColorOf, normalizedHexColorSchema, type HexColor } from '../validators/tenant.validator.js'

/**
 * An organization's accent color as signers see it: on the signing, embed, and
 * answerer pages (primary buttons, links, focus rings, a line under the
 * header) and on the buttons and links of signer email. Never the attention
 * orange on signing fields, which has to mean "fill this in" for every
 * organization.
 *
 * Contrast follows WCAG 2.1: button text needs 4.5:1 against the accent, link
 * text 4.5:1 against the page (white, or the dark surface of a dark embed),
 * and a focus ring 3:1. A color that can't carry readable button text either
 * way falls back to PixelSign's own accent.
 */
/**
 * Button text on an organization's accent: the design system's white (`light`)
 * or ink (`dark`). Not a page scheme (`ColorScheme`): `light` here is light
 * text, not a light page.
 */
export const ACCENT_FOREGROUNDS = ['light', 'dark'] as const

export type AccentForeground = (typeof ACCENT_FOREGROUNDS)[number]

export interface AccentTheme {
  /** The organization's color, for primary buttons (and the header line). */
  accent: HexColor
  /** A shade for hover that keeps the button text readable. */
  hover: HexColor
  /** Button text: `light` (the design system's white) or `dark` (its ink). */
  foreground: AccentForeground
  /** Whether links can use the accent (else they keep the default link color). */
  links: boolean
  /** Whether focus rings can use the accent (else they keep the default ring). */
  ring: boolean
}

/** WCAG's thresholds: body text, and non-text UI such as focus indicators. */
const TEXT_CONTRAST = 4.5
const UI_CONTRAST = 3

/**
 * Luminance of the design system's white (`--ds-white`) and ink (`--ds-gray-900`,
 * #111827), the two candidates for button text, and of the white page behind links.
 */
const WHITE = 1
const INK = relativeLuminance('#111827')

/** The page links and focus rings sit on: white, or a dark embed's lightest surface (`--ds-gray-925`). */
export type AccentSurface = ColorScheme

const SURFACE_LUMINANCE: Record<AccentSurface, number> = {
  light: WHITE,
  dark: relativeLuminance('#0e121a'),
}

type Rgb = [number, number, number]

function rgbOf(hex: string): Rgb | null {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex)
  if (!match) return null
  return [parseInt(match[1] ?? '0', 16), parseInt(match[2] ?? '0', 16), parseInt(match[3] ?? '0', 16)]
}

function hexOf([r, g, b]: Rgb): HexColor {
  return normalizedHexColorSchema.parse(`#${[r, g, b].map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`)
}

/** WCAG relative luminance of a `#rrggbb` color (0 for black, 1 for white). */
export function relativeLuminance(hex: string): number {
  const rgb = rgbOf(hex)
  if (!rgb) return 0
  const [r, g, b] = rgb.map((channel) => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0)
}

/** WCAG contrast ratio of two luminances (1 to 21). */
export function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

/** `hex` moved `amount` (0–1) of the way toward white or black. */
function mix(hex: HexColor, toward: 'white' | 'black', amount: number): HexColor {
  const rgb = rgbOf(hex)
  if (!rgb) return hex
  const target = toward === 'white' ? 255 : 0
  const mixed: Rgb = [
    rgb[0] + (target - rgb[0]) * amount,
    rgb[1] + (target - rgb[1]) * amount,
    rgb[2] + (target - rgb[2]) * amount,
  ]
  return hexOf(mixed)
}

/**
 * The theme for an organization's accent color, or null to keep
 * PixelSign's (no color set, not a `#rrggbb` color, or one no button text is
 * readable on). White button text is preferred when it's readable; hover
 * darkens under white text and lightens under ink, so it stays readable too.
 * Links and rings are checked against `surface`; buttons carry their own text.
 */
export function accentTheme(
  accentColor: string | null | undefined,
  surface: AccentSurface = 'light',
): AccentTheme | null {
  const accent = accentColorOf(accentColor ?? null)
  if (!accent) return null
  const luminance = relativeLuminance(accent)
  let foreground: AccentTheme['foreground']
  if (contrastRatio(luminance, WHITE) >= TEXT_CONTRAST) foreground = 'light'
  else if (contrastRatio(luminance, INK) >= TEXT_CONTRAST) foreground = 'dark'
  else return null
  return {
    accent,
    hover: foreground === 'light' ? mix(accent, 'black', 0.12) : mix(accent, 'white', 0.18),
    foreground,
    links: contrastRatio(luminance, SURFACE_LUMINANCE[surface]) >= TEXT_CONTRAST,
    ring: contrastRatio(luminance, SURFACE_LUMINANCE[surface]) >= UI_CONTRAST,
  }
}
